// Active organization context: role and permissions come from the server for the org in the
// URL. The UI only hides what the user cannot do — the API enforces it (CLAUDE.md rule 2).
import type { OrgContextResponse, Permission } from '@twinlink/shared';
import { useQuery } from '@tanstack/react-query';
import { useMatch } from 'react-router-dom';
import { api } from './api';
import { getLastOrgId, useAuth } from './auth';

export const orgKeys = {
  context: (orgId: string) => ['org', orgId, 'context'] as const,
};

export function useOrgContext(orgId: string | undefined) {
  return useQuery({
    queryKey: orgKeys.context(orgId ?? 'none'),
    queryFn: () => api<OrgContextResponse>(`/orgs/${orgId}`),
    enabled: !!orgId,
    staleTime: 60_000,
    retry: false,
  });
}

/** Org id from the URL (`/o/:orgId/...`), else the last org the user worked in. */
export function useActiveOrgId(): string | undefined {
  const match = useMatch('/o/:orgId/*');
  const me = useAuth((s) => s.me);
  if (match?.params.orgId) return match.params.orgId;
  if (me?.impersonation) return me.impersonation.orgId;
  const last = getLastOrgId();
  if (me?.user.platformRole) return last ?? undefined;
  const orgs = me?.memberships ?? [];
  return orgs.find((o) => o.orgId === last)?.orgId ?? orgs[0]?.orgId;
}

/** Permissions of the caller in the active org (empty set while loading or outside an org). */
export function usePermissions(): Set<Permission> {
  const orgId = useActiveOrgId();
  const { data } = useOrgContext(orgId);
  return new Set((data?.permissions ?? []) as Permission[]);
}

export function usePermission(permission: Permission): boolean {
  return usePermissions().has(permission);
}

export function useOrgId(): string {
  const orgId = useActiveOrgId();
  if (!orgId) throw new Error('No active organization');
  return orgId;
}
